import { useTranslation } from 'react-i18next';
import {
  ArrowPathIcon,
  InformationCircleIcon,
} from '@heroicons/react/24/outline';
import { tWhatsAppWebReadOnlyBannerProps } from './WhatsAppWebReadOnlyBanner.types';
import './WhatsAppWebReadOnlyBanner.styles.scss';

/**
 * Read-only hint banner shown in place of the composer when a chat is viewed
 * on the WhatsApp Web channel for a workspace whose WhatsApp Web account no
 * longer exists (INT-941 / U2).
 *
 * It replaces the text input, action-icon toolbar, attachments, emoji/plus
 * controls, and send button — those are unmounted by `MessageBar.content` so
 * suppressed controls leave the tab order. The banner itself is informational
 * and rendered as a `status` region so screen readers announce it when it
 * takes the place of a composer the agent may have focused.
 */
export default function WhatsAppWebReadOnlyBanner(
  props: tWhatsAppWebReadOnlyBannerProps,
) {
  const { hasWaba, showRetry, onRetry, onSwitchToWhatsAppBusiness } = props;
  const { t } = useTranslation('chat/chat');

  const message = hasWaba
    ? t('whatsAppWebReadOnly.bannerWithWaba')
    : t('whatsAppWebReadOnly.bannerNoWaba');

  return (
    <div className="WhatsAppWebReadOnlyBanner" role="status">
      <InformationCircleIcon className="WhatsAppWebReadOnlyBanner__Icon" />
      <p className="WhatsAppWebReadOnlyBanner__Text">{message}</p>
      {hasWaba && (
        <button
          type="button"
          onClick={onSwitchToWhatsAppBusiness}
          className="WhatsAppWebReadOnlyBanner__SwitchButton"
        >
          {t('whatsAppWebReadOnly.switchButton')}
        </button>
      )}
      {showRetry && (
        <button
          type="button"
          onClick={onRetry}
          className="WhatsAppWebReadOnlyBanner__RetryButton"
        >
          <ArrowPathIcon className="WhatsAppWebReadOnlyBanner__RetryButton__Icon" />
          {t('whatsAppWebReadOnly.retry')}
        </button>
      )}
    </div>
  );
}
